<template>
  <div class="min-h-screen bg-gray-50">
    <HeaderDefault />

    <!-- Hero Section -->
    <section class="bg-gradient-to-b from-primary to-primary/70 py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl">
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Get in Touch
          </h1>
          <p class="text-xl text-primary-light leading-relaxed">
            Have questions? We're here to help. Reach out to our team through any of the channels below.
          </p>
        </div>
      </div>
    </section>

    <!-- Contact Information -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <!-- Email -->
          <div class="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
            <div class="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <icon name="heroicons:envelope" class="w-6 h-6 text-primary" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
            <p class="text-gray-600 mb-4">For general inquiries and support</p>
            <a href="mailto:hello@academicalerts.org" class="text-primary hover:text-primary/90 font-medium">
              hello@academicalerts.org
            </a>
          </div>

          <!-- Location -->
          <div class="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow hidden">
            <div class="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <icon name="heroicons:map-pin" class="w-6 h-6 text-primary" />
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Visit Us</h3>
            <p class="text-gray-600 mb-4">Our office location</p>
            <address class="text-primary not-italic">
              MUTBI, MIT Campus<br />
              Manipal, Karnataka<br />
              India - 576104
            </address>
          </div>
        </div>

        <!-- Contact Form -->
        <div class="max-w-2xl mx-auto mt-16 hidden">
          <div class="bg-white rounded-xl shadow-sm p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
            <form @submit.prevent="submitForm" class="space-y-6">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div>
                <label for="message" class="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  id="message"
                  v-model="form.message"
                  rows="4"
                  required
                  class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                ></textarea>
              </div>

              <button
                type="submit"
                :disabled="isSubmitting"
                class="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
              >
                <span>{{ isSubmitting ? 'Sending...' : 'Send Message' }}</span>
                <icon 
                  v-if="isSubmitting" 
                  name="heroicons:arrow-path" 
                  class="w-5 h-5 ml-2 animate-spin"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
const form = ref({
  name: '',
  email: '',
  message: ''
});

const isSubmitting = ref(false);

async function submitForm() {
  isSubmitting.value = true;
  try {
    // Add your form submission logic here
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated delay
    alert('Message sent successfully!');
    form.value = { name: '', email: '', message: '' };
  } catch (error) {
    alert('Failed to send message. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}

useHead({
  title: 'Contact Us - Academic Alerts',
  meta: [
    { 
      name: 'description', 
      content: 'Get in touch with Academic Alerts. We\'re here to help with your questions and feedback.' 
    }
  ]
});
</script>